import { useState } from "react";
import { Head, router } from "@inertiajs/react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/Button";
import PlanHeader from "../components/PlanHeader";
import SubmitMessageCard from "../TopDown/components/SubmitMessageCard";
import ReviewersCard from "../TopDown/components/ReviewersCard";
import ReviewTaskCard from "../TopDown/components/ReviewTaskCard";
import ByMonthDiffTable from "./components/ByMonthDiffTable";
import { monthlySummaryText, type ByMonthDiff } from "./components/byMonthDiff";
import type { Row, Channel, MonthlyRevisionSubmit } from "./byMonthTypes";
import { type Person, type Task } from "@/pages/Tasks/task";
import { plan_bottom_up_by_month_channel_path, plan_bottom_up_by_month_edit_path, submissions_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";

interface Props {
  plan: { id: string; name: string; slug: string; begin_date: string; end_date: string; currency: string | null; revenue_target: number; org_node: { id: string; name: string } };
  months: string[];
  rows: Row[];
  current_channel: Channel;
  diff: ByMonthDiff;
  submit_task: Task | null;
  people: Person[];
  monthly_revision_submit: MonthlyRevisionSubmit | null;
}

// The doer's submit page for one channel's monthly plan, same anatomy as
// Plan/BottomUp/ProductPricingsSubmit: the message on the left, who reviews
// on the right (the task's card, or a reviewer picker for a revision), and
// the changes against the published cells below.
export default function ByMonthSubmit({ plan, months, rows, current_channel, diff, submit_task, people, monthly_revision_submit }: Props) {
  const market = useMarket();
  const [message, setMessage] = useState("");
  const [reviewerIds, setReviewerIds] = useState<string[]>(monthly_revision_submit?.default_reviewer_ids ?? []);
  const [changesOnly, setChangesOnly] = useState(true);
  const hasReviewers = submit_task ? submit_task.reviewers.length > 0 : reviewerIds.length > 0;

  const hint = hasReviewers
    ? "Published numbers stay as they are until a reviewer approves."
    : submit_task ? "No reviewer on this task — this will publish the monthly plan right away." : null;
  const submitLabel = hasReviewers ? "Submit for review" : submit_task ? "Mark completed" : "Publish changes";
  const showPath = plan_bottom_up_by_month_channel_path(market.slug, plan.slug, current_channel.slug);

  const submit = () => {
    if (submit_task) {
      router.post(submissions_path(), { task_id: submit_task.id, message, back_to: showPath });
    } else if (monthly_revision_submit) {
      router.post(submissions_path(), {
        kind: "monthly_plan",
        plan_id: monthly_revision_submit.plan_id,
        channel_id: monthly_revision_submit.channel_id,
        reviewer_ids: reviewerIds,
        message,
        back_to: showPath,
      });
    }
  };

  return (
    <AppLayout header={<PlanHeader name={plan.name} orgNodeName={plan.org_node.name} revenueTarget={plan.revenue_target} currency={plan.currency} beginDate={plan.begin_date} endDate={plan.end_date} />}>
      <Head title={`${current_channel.name} monthly plan — Submit`} />
      <div className="flex items-start gap-4">
        <div className="min-w-0 lg:w-3/5">
          <SubmitMessageCard
            label={hasReviewers ? "Message to your reviewer" : "Message"}
            placeholder="What changed and why"
            hint={hint}
            warning={!hasReviewers}
            message={message}
            onChange={setMessage}
            submitLabel={submitLabel}
            onSubmit={submit}
            onCancel={() => router.visit(plan_bottom_up_by_month_edit_path(market.slug, plan.slug, current_channel.slug))}
          />
        </div>
        {submit_task
          ? <ReviewTaskCard task={submit_task} people={people} />
          : monthly_revision_submit && (
            <ReviewersCard people={monthly_revision_submit.people} selectedIds={reviewerIds} onChange={setReviewerIds} />
          )}
      </div>
      <div className="mt-6 mb-4 rounded-lg border px-4 py-2.5 text-sm text-foreground" style={{ background: "var(--info-surface)", borderColor: "var(--border)" }}>
        <span className="font-semibold">{current_channel.name} · Monthly Plan.</span> {monthlySummaryText(diff.summary)}.
        {diff.summary.any_changes && " Green is what this submission sets and red is the published value it replaces."}
      </div>
      <div className="overflow-hidden rounded-md border bg-card">
        <div className="flex items-center justify-between border-b px-4 py-2 text-[12px] text-muted-foreground">
          <span>{current_channel.name}</span>
          <Button variant="ghost" size="sm" className="h-7 text-[12px]" onClick={() => setChangesOnly((v) => !v)}>
            {changesOnly ? "Show all rows" : "Show changes only"}
          </Button>
        </div>
        <ByMonthDiffTable rows={rows} months={months} diff={diff} changesOnly={changesOnly} emptyMessage={changesOnly ? "No changed rows." : `No products for ${current_channel.name}.`} />
      </div>
    </AppLayout>
  );
}
